import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ScrollReveal from '@/components/ScrollReveal';
import {
  Shield,
  Lock,
  Settings,
  TrendingUp,
  Target,
  CheckCircle,
  ArrowRight,
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const philosophyItems = [
  {
    icon: Shield,
    title: 'Security-first',
    subtitle: 'Controlled access, data governance, least privilege principles',
    items: [
      'Role-based access control (RBAC)',
      'Data classification and handling',
      'Encrypted communications and storage',
      'Zero-trust architecture patterns',
    ],
  },
  {
    icon: Lock,
    title: 'Governed outputs',
    subtitle: 'Evaluation, guardrails, and auditability',
    items: [
      'Continuous quality evaluation',
      'Source grounding and citations',
      'Complete audit trails',
      'Policy-driven guardrails',
    ],
  },
  {
    icon: Settings,
    title: 'Operational reality',
    subtitle: 'Reliability, monitoring, cost control, ownership',
    items: [
      'Production SLAs and monitoring',
      'Cost tracking and optimisation',
      'Clear operational ownership',
      'Incident response procedures',
    ],
  },
  {
    icon: TrendingUp,
    title: 'Commercial outcomes',
    subtitle: 'Clear ROI and measurable impact',
    items: [
      'Defined success metrics',
      'Baseline and target measurement',
      'Business case validation',
      'Continuous improvement cycles',
    ],
  },
];

const engagementPhases = [
  {
    number: '1',
    title: 'Discovery & ROI mapping',
    subtitle: 'Identify workflows worth automating and define success metrics',
    duration: '1-2 weeks',
    activities: [
      'Workshop with stakeholders to map current workflows',
      'Identify high-friction, high-value automation opportunities',
      'Define success metrics and baseline measurements',
      'Build business case with ROI projection',
      'Assess data readiness and integration requirements',
    ],
  },
  {
    number: '2',
    title: 'Prototype with guardrails',
    subtitle: 'Deliver a working model quickly, but with governance from day one',
    duration: '2-4 weeks',
    activities: [
      'Build working prototype of AI workflow',
      'Implement core guardrails and evaluation framework',
      'Demonstrate capabilities with sample data',
      'Gather stakeholder feedback',
      'Validate technical approach and architecture',
    ],
  },
  {
    number: '3',
    title: 'Pilot & integrate',
    subtitle: 'Integrate with your systems; validate outcomes and performance',
    duration: '4-8 weeks',
    activities: [
      'Integrate with enterprise systems and data sources',
      'Deploy to limited user group for pilot testing',
      'Measure performance against success metrics',
      'Refine models based on real-world usage',
      'Validate security, compliance, and operational requirements',
    ],
  },
  {
    number: '4',
    title: 'Production rollout',
    subtitle: 'Deploy with monitoring, security controls, and operational readiness',
    duration: '2-4 weeks',
    activities: [
      'Full production deployment with monitoring and alerting',
      'User training and documentation',
      'Operational runbooks and support procedures',
      'Continuous evaluation and improvement cycles',
      'Knowledge transfer to internal teams',
    ],
  },
];

const differentiators = [
  {
    title: 'Founder-led delivery',
    description:
      'You work directly with senior founders, not junior consultants learning on your project. Our founding team stays hands-on in architecture, value definition, and execution.',
  },
  {
    title: 'Production-grade from day one',
    description:
      'Most AI initiatives fail when they meet production reality: data boundaries, compliance rules, messy workflows, operational ownership. We design for these constraints from the start.',
  },
  {
    title: 'Deep enterprise experience',
    description:
      '60+ years combined experience delivering mission-critical systems at Ericsson, IBM, Microsoft, GlobalLogic, and national government programmes. We understand enterprise operations.',
  },
  {
    title: 'Governance and compliance built-in',
    description:
      "Security, audit trails, source grounding, and policy controls aren't afterthoughts — they're core to our platform design from the first prototype.",
  },
  {
    title: 'Measurable business outcomes',
    description:
      'We focus on concrete, measurable wins: cycle time reduction, operational efficiency, decision velocity improvements, risk reduction. Not vague "innovation" claims.',
  },
  {
    title: 'Multi-industry expertise',
    description:
      "We've built AI systems across FinTech, Pharma, Telecom, Recruitment, and Government. We understand the specific compliance and operational requirements of regulated industries.",
  },
];

const principles = [
  {
    title: 'Transparency over hype',
    description:
      "We're honest about what AI can and cannot do. We set realistic expectations and deliver on our commitments.",
  },
  {
    title: 'Security over speed',
    description:
      'We never compromise on security or compliance to ship faster. Governance is non-negotiable.',
  },
  {
    title: 'Outcomes over outputs',
    description:
      "Success isn't measured by lines of code or model accuracy scores — it's measured by business impact.",
  },
  {
    title: 'Partnership over consulting',
    description:
      "We're invested in your success. We work as an extension of your team, not distant advisors.",
  },
  {
    title: 'Knowledge transfer over dependency',
    description:
      'We build your internal capability to own and operate AI systems. Our goal is to make you self-sufficient.',
  },
];

export default function AboutPage() {
  useEffect(() => {
    ScrollTrigger.refresh();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Page Header */}
        <section className="pt-44 pb-8 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
          <div className="container-custom relative z-10">
            <ScrollReveal className="text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                About CoDRIVER LABS
              </h1>
              <p className="text-lg sm:text-xl text-slate-600">
                Enterprise AI built the way mission-critical systems should be
                built
              </p>
            </ScrollReveal>
          </div>
        </section>

        {/* Mission */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal>
              <div className="max-w-4xl mx-auto text-center">
                <span className="text-primary text-sm font-medium uppercase tracking-wider">
                  Our Mission
                </span>
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-slate-900 mt-4 mb-6">
                  Deliver enterprise-grade AI systems that create measurable
                  operational and decision-making advantage — without compromising
                  security, compliance, or reliability.
                </h2>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Vision */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <ScrollReveal>
              <div className="max-w-4xl mx-auto">
                <span className="text-primary text-sm font-medium uppercase tracking-wider">
                  Our Vision
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mt-4 mb-6">
                  AI becomes a practical co-driver for modern organisations
                </h2>
                <p className="text-slate-600 leading-relaxed">
                  We envision a future where AI assistants and workflows are
                  trusted members of enterprise operations — not experimental
                  add-ons, but essential infrastructure that organisations depend
                  on daily.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Philosophy */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <span className="text-primary text-sm font-medium uppercase tracking-wider">
                Our Philosophy
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
                "Build AI like enterprise systems"
              </h2>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
              {philosophyItems.map((item, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-card h-full">
                    <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                      <item.icon className="w-6 h-6 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm mb-4">
                      {item.subtitle}
                    </p>
                    <ul className="space-y-2">
                      {item.items.map((listItem, i) => (
                        <li
                          key={i}
                          className="flex items-start gap-2 text-slate-500 text-sm"
                        >
                          <span className="w-1 h-1 bg-primary rounded-full mt-2 flex-shrink-0" />
                          {listItem}
                        </li>
                      ))}
                    </ul>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* How We Engage */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <span className="text-primary text-sm font-medium uppercase tracking-wider">
                Our Process
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-4">
                How We Engage
              </h2>
              <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
                We follow a structured, risk-managed approach to AI deployment
                that balances speed with governance
              </p>
            </ScrollReveal>

            <div className="max-w-4xl mx-auto space-y-8">
              {engagementPhases.map((phase, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="flex gap-6">
                    {/* Number */}
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 bg-primary rounded-full flex items-center justify-center text-xl font-bold text-white">
                        {phase.number}
                      </div>
                      {index < engagementPhases.length - 1 && (
                        <div className="w-0.5 h-full bg-primary/20 mx-auto mt-4" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="pb-8">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="text-xl font-semibold text-slate-900">
                          {phase.title}
                        </h3>
                        <span className="px-3 py-1 bg-primary/10 text-primary text-xs rounded-full">
                          {phase.duration}
                        </span>
                      </div>
                      <p className="text-slate-600 mb-4">
                        {phase.subtitle}
                      </p>
                      <div className="bg-white rounded-lg p-4 border border-slate-200">
                        <h4 className="text-slate-900 font-medium text-sm mb-2">
                          Activities:
                        </h4>
                        <ul className="space-y-1">
                          {phase.activities.map((activity, i) => (
                            <li
                              key={i}
                              className="flex items-start gap-2 text-slate-500 text-sm"
                            >
                              <CheckCircle className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                              {activity}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* What Makes Us Different */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                What Makes Us Different
              </h2>
            </ScrollReveal>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {differentiators.map((item, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="bg-white rounded-xl p-6 border border-slate-200 shadow-card h-full">
                    <h3 className="text-lg font-semibold text-slate-900 mb-3">
                      {item.title}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {item.description}
                    </p>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Our Principles */}
        <section className="section-padding bg-slate-50">
          <div className="container-custom">
            <ScrollReveal className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Our Principles
              </h2>
            </ScrollReveal>

            <div className="max-w-3xl mx-auto space-y-6">
              {principles.map((principle, index) => (
                <ScrollReveal key={index} delay={index * 0.1}>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                      <Target className="w-4 h-4 text-primary" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-slate-900 mb-1">
                        {principle.title}
                      </h3>
                      <p className="text-slate-600">
                        {principle.description}
                      </p>
                    </div>
                  </div>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="section-padding">
          <div className="container-custom">
            <ScrollReveal className="text-center max-w-2xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
                Ready to build AI that works in production?
              </h2>
              <p className="text-slate-600 mb-8">
                Let's discuss your specific requirements and chart a path to
                measurable value.
              </p>
              <Link
                to="/contact"
                className="btn-primary inline-flex items-center gap-2 group"
              >
                Book a Discovery Call
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </ScrollReveal>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
